/* eslint-disable @typescript-eslint/no-explicit-any */
import { Subject, skip, takeUntil } from 'rxjs';

import { CommonModule, isPlatformBrowser, isPlatformServer } from '@angular/common';
import { Component, computed, inject, Inject, NgZone, OnDestroy, OnInit, Optional, PLATFORM_ID, REQUEST, REQUEST_CONTEXT, signal, TransferState } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';

import { environment } from '../../environments/environment';
import { SharingShellContext } from '../../sharing-page-shell';
import { InfluencerStatsComponent } from '../influencer-stats/influencer-stats.component';
import { PageSpinnerService } from '../shared/components/page-spinner/services/page-spinner.service';
import {
  buildBuzzwavePublicUrl,
  BUZZWAVE_PUBLIC_PATHS,
  fetchWithBootstrapAuth,
  setBrowserPreviewToken,
} from '../shared/helpers/buzzwave-public-api';
import { FB_APP_ID } from '../shared/helpers/constants';
import { resolveLandingPageConversionOrigin } from '../shared/helpers/conversion-origin';
import { isNullOrUndefined, isNullOrWhiteSpace, replaceMergeTags } from '../shared/helpers/helper-functions';
import { FacebookService } from '../shared/services/facebook.service';
import { LandingPageStateService } from '../shared/services/landing-page-state.service';
import { TwitterService } from '../shared/services/twitter.service';
import { legacyCopy } from './helpers/helpers';
import { loadLandingPageFingerprint } from './helpers/fingerprint';
import {
  CAMPAIGNDATA_KEY, CAMPAIGNPARTICIPANTID_KEY, ERRORMESSAGE_KEY, INACTIVEPAGE_KEY, ISBOT_KEY,
  ISERROR_KEY, ISSHORTLINK_KEY, REDIRECTURL_KEY, REQUESTPARSE_KEY, REQUESTURL_KEY, SHAREIMAGEURL_KEY
} from './landing-page.state';

interface ShareNetwork {
  name: string;
  icon: string;
  iconBw: string;
  class: string;
  imgIcon?: string;
  selected?: boolean;
  display: boolean;
  appUrl?: string;
  fallbackUrl?: string;
}

function isCampaignData(value: unknown): boolean {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

// function buildLandingPageErrorPayload(
//   message: string,
//   stack: string | null = null,
//   context: {
//     campaignId?: string;
//     campaignParticipantId?: string;
//     requestUrl?: string;
//     campaignLookupUrl?: string;
//     requestParse?: {
//       rawPath?: string;
//       rawQueryString?: string;
//       campaignId?: string;
//       campaignParticipantId?: string;
//     };
//   } = {},
// ) {
//   return {
//     message,
//     stack,
//     source: 'buzzwave-go-landing-page',
//     campaignId: context.campaignId ?? '',
//     campaignParticipantId: context.campaignParticipantId ?? '',
//     requestUrl: context.requestUrl ?? '',
//     campaignLookupUrl: context.campaignLookupUrl ?? '',
//     requestParse: context.requestParse ?? {},
//     timestamp: new Date().toISOString(),
//   };
// }

enum ShareType {
  Link = 'Link',
  Email = 'Email',
  Facebook = 'Facebook',
  Instagram = 'Instagram',
  LinkedIn = 'LinkedIn',
  Slack = 'Slack',
  Teams = 'Teams',
  Twitter = 'Twitter',
  WhatsApp = 'WhatsApp',
}

@Component({
  selector: 'app-landing-page',
  standalone: true,
  imports: [
    CommonModule,
    MatInputModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    FormsModule,
    InfluencerStatsComponent,
    MatIconModule,
  ],
  templateUrl: './landing-page.component.html',
  styleUrl: './landing-page.component.scss',
})
export class LandingPageComponent implements OnInit, OnDestroy {
  public buttonText = signal<string>('');
  public campaignId = '';
  public campaignParticipantId = '';
  public clientAccountId = '';
  public company = '';
  public eventImageUrl = signal<string>('');
  public eventName = signal<string>('');
  public sponsor = signal<{ name: string; logo: string; message: string; url: string } | null>(null);
  public incentiveTCs = signal<string>('');
  public incentiveText = signal<string>('');
  public isLoading = signal<boolean>(true);
  public showLeaderboard = signal<boolean>(false);
  public enableLeaderboard = signal<boolean>(false);
  public canViewConvertedCompanies = signal(false);
  public canViewInfluencerStats = computed(() => this.canViewConvertedCompanies() || this.enableLeaderboard());
  private readonly route = inject(ActivatedRoute, { optional: true });
  public jobTitle = '';

  public postBody = signal<string>(''); // original used for determining which urls to shorten
  public linkedInSharing = signal(false);
  public linkedInShareError = signal('');
  public postBodyTwitter = signal<string>(''); // original used for determining which urls to shorten
  public postBodyWithShortLinks = signal<string>('');
  public postBodyTwitterWithShortLinks = signal<string>('');

  // public postForwardUrl = signal<string>('');
  public postImageUrl = signal<string>('');
  public showBuzzwaveBranding = signal<boolean>(false);
  public campaignTitle = signal<string>('');
  public campaignText = signal<string>('');
  public useIncentive = signal<boolean>(false);
  public usePersonalisation = signal<boolean>(false);
  public influencerMessage = signal<string>('');
  public shortLinksAcquiredForNetworks: {
    campaignShareId: string;
    networkName: string;
    postBody: string;
    shortLinkIds: string[];
  }[] = [];
  private selectedNetworkCampaignShareId = '';
  private fingerprint = '';
  private fingerprintReady: Promise<void> = Promise.resolve();
  private readonly pendingShortLinks = new Map<string, Promise<void>>();
  private readonly ngZone = inject(NgZone);
  private sourceSequenceId?: string;
  private sourceSequenceStepId?: string;

  public selectedNetwork = signal<ShareNetwork>({
    name: 'LinkedIn',
    class: 'linkedin',
    icon: 'assets/linkedin.png',
    iconBw: 'assets/linkedin_bw.png',
    selected: true,
    display: true,
    appUrl: '',
  });

  public filteredNetworks = computed(() => {
    const selectedNetwork = this.selectedNetwork();
    return this.shareNetworks().filter((n) => n.display && n.name !== selectedNetwork.name);
  });

  public shareNetworks = signal<ShareNetwork[]>([
    {
      name: ShareType.LinkedIn,
      class: 'linkedin',
      icon: 'assets/linkedin.png',
      iconBw: 'assets/linkedin_bw.png',
      display: true,
    },
    {
      name: ShareType.Facebook,
      class: 'facebook',
      icon: 'assets/facebook.png',
      iconBw: 'assets/facebook_bw.png',
      display: true,
    },
    {
      name: ShareType.Instagram,
      class: 'instagram',
      icon: 'assets/instagram.png',
      iconBw: 'assets/instagram_bw.png',
      display: true,
      appUrl: 'instagram://user',
      fallbackUrl: 'https://www.instagram.com',
    },
    {
      name: ShareType.Twitter,
      class: 'twitter',
      icon: 'assets/twitter.png',
      iconBw: 'assets/twitter_bw.png',
      display: true,
    },
    {
      name: ShareType.WhatsApp,
      class: 'whatsapp',
      icon: 'assets/whatsapp.png',
      iconBw: 'assets/whatsapp_bw.png',
      display: true,
      appUrl: 'whatsapp://',
    },
    {
      name: ShareType.Slack,
      class: 'slack',
      icon: 'assets/slack.png',
      iconBw: 'assets/slack_bw.png',
      display: true,
      appUrl: 'slack://open',
      fallbackUrl: 'https://slack.com',
    },
    {
      name: ShareType.Teams,
      class: 'teams',
      imgIcon: 'rounded',
      icon: 'assets/teams.png',
      iconBw: 'assets/teams_bw.png',
      display: true,
      appUrl: 'msteams://',
      fallbackUrl: 'https://teams.microsoft.com/',
    },
    {
      name: ShareType.Email,
      class: 'generic',
      icon: 'assets/email.png',
      iconBw: 'assets/email.png',
      display: true,
    },
    {
      name: ShareType.Link,
      class: 'generic',
      icon: 'assets/copy.png',
      iconBw: 'assets/copy.png',
      display: true,
    },
  ]);

  private buzzwaveS3Url = '';
  private isBot = false;
  private isShortLink = false;
  private campaignData: any = null;
  private readonly onDestroy$ = new Subject();
  private currentRequestUrl = '';
  private currentCampaignLookupUrl = '';
  private currentRequestParse = {
    rawPath: '',
    rawQueryString: '',
    campaignId: '',
    campaignParticipantId: '',
  };
  private skipLandingPageProcessing = false;

  constructor(
    private spinner: PageSpinnerService,
    private titleService: Title,
    private metaService: Meta,
    private fbService: FacebookService,
    private twitterService: TwitterService,
    private transferState: TransferState,
    private router: Router,
    private state: LandingPageStateService,
    @Inject(PLATFORM_ID) private platformId: object,
    @Optional() @Inject(REQUEST) private request: Request | null,
  ) {
    this.buzzwaveS3Url = environment.buzzwaveClientAssetsS3Url;
    this.state.reset();
    this.showLeaderboard = this.state.showLeaderboard;
    this.enableLeaderboard = this.state.enableLeaderboard;
    this.canViewConvertedCompanies = this.state.canViewConvertedCompanies;
    this.captureSourceSequenceContext();
    const requestContext = inject(REQUEST_CONTEXT, { optional: true }) as SharingShellContext | null;
    this.skipLandingPageProcessing = this.isStaticAssetRequest(this.router.url ?? '') ||
      (isPlatformServer(this.platformId) && requestContext?.sharingShell === true);
    if (this.skipLandingPageProcessing) {
      return;
    }
    const inactivePageState = this.transferState.get(INACTIVEPAGE_KEY, null);
    if (inactivePageState) {
      this.spinner.resetLoader();
      if (isPlatformBrowser(this.platformId)) {
        void this.router.navigate(['/inactive'], { state: inactivePageState });
      }
      return;
    }
    this.spinner.showLoader();
    const userAgent = (this.request?.headers?.get('user-agent') || '').toLowerCase();
    this.isBot =
      userAgent.includes('facebookexternalhit') ||
      userAgent.includes('twitterbot') ||
      userAgent.includes('pinterest') ||
      userAgent.includes('slackbot') ||
      userAgent.includes('linkedinbot');
    const isFacebookOrTwitterBot =
      userAgent.includes('facebookexternalhit') || userAgent.includes('twitterbot');
    this.transferState.set(ISBOT_KEY, this.isBot);

    // this.campaignFound.set(this.transferState.get(CAMPAIGNFOUND_KEY, false));
    this.campaignData = this.transferState.get(CAMPAIGNDATA_KEY, -1);
    this.isShortLink = this.transferState.get(ISSHORTLINK_KEY, false);
    const participantRequired = this.campaignData?.showCustomImage === true &&
      isNullOrWhiteSpace(this.transferState.get(CAMPAIGNPARTICIPANTID_KEY, ''));
    const canDisplayCampaign = isCampaignData(this.campaignData) && !this.isShortLink && !participantRequired &&
      !this.transferState.get(ISERROR_KEY, false) && !this.transferState.get(REDIRECTURL_KEY, '');
    // Short links still need server metadata for social crawlers, even when
    // their browser journey redirects instead of displaying the sharing view.
    if (isCampaignData(this.campaignData) && (isPlatformServer(this.platformId) || canDisplayCampaign)) {
      this.initializeCampaignDisplayFromData(isFacebookOrTwitterBot);
    }
    if (canDisplayCampaign) {
      this.isLoading.set(false);
      this.spinner.hideLoader();
    }

    if (isPlatformBrowser(this.platformId)) {
      this.removePreviewTokenFromBrowserUrl();
      if (this.skipLandingPageProcessing) {
        return;
      }

      const redirectUrl = this.transferState.get(REDIRECTURL_KEY, '');
      if (redirectUrl !== '') {
        window.location.replace(redirectUrl);
        return;
      }

      this.isBot = this.transferState.get(ISBOT_KEY, false);
      this.isShortLink = this.transferState.get(ISSHORTLINK_KEY, false);

      window.onbeforeunload = function () {
        sessionStorage.setItem('buzzwave-origin', window.location.href);
      };

      window.onload = async function onLoad() {
        if (window.location.href == sessionStorage.getItem('buzzwave-origin')) {
          sessionStorage.removeItem('buzzwave-campaign-metadata');
        }
      };
    }
  }

  private redirectToErrorPage(
    errorMessage = 'We could not load this campaign. Please try again later.',
  ): void {
    this.spinner.hideLoader();
    this.transferState.set(ISERROR_KEY, true);
    this.transferState.set(ERRORMESSAGE_KEY, errorMessage);

    const requestParse = this.currentRequestParse.rawPath
      ? this.normalizeRequestParse(this.currentRequestParse)
      : this.transferState.get(REQUESTPARSE_KEY, this.currentRequestParse);
    const campaignId = requestParse.campaignId || this.campaignId;

    if (isPlatformServer(this.platformId)) {
      console.error('[SSR] Landing page load error:', {
        errorMessage,
        campaignId,
      });
    }

    if (isPlatformBrowser(this.platformId)) {
      this.router.navigate(['/error'], { state: { errorMessage } });
    }
  }

  private normalizeRequestParse(requestParse: {
    rawPath: string;
    rawQueryString: string;
    campaignId?: string;
    campaignParticipantId?: string;
  }): {
    rawPath: string;
    rawQueryString: string;
    campaignId: string;
    campaignParticipantId: string;
  } {
    const parsedFromPath = this.parseRequestContext(requestParse.rawPath);
    return {
      rawPath: requestParse.rawPath,
      rawQueryString: requestParse.rawQueryString,
      campaignId: requestParse.campaignId || parsedFromPath.campaignId || this.campaignId || '',
      campaignParticipantId:
        requestParse.campaignParticipantId ||
        parsedFromPath.campaignParticipantId ||
        this.campaignParticipantId ||
        '',
    };
  }

  //#region ngOnInit etc
  async ngOnInit(): Promise<void> {
    this.route?.data.pipe(skip(1), takeUntil(this.onDestroy$)).subscribe(data => {
      const campaign = data['campaignData'];
      this.state.reset();
      if (!isCampaignData(campaign)) return;
      this.campaignData = campaign;
      this.captureSourceSequenceContext();
      this.initializeCampaignDisplayFromData();
      this.isLoading.set(false);
    });
    if (this.skipLandingPageProcessing) {
      return;
    }

    if (this.transferState.get(INACTIVEPAGE_KEY, null)) {
      return;
    }

    if (isPlatformServer(this.platformId)) {
      await this.loadCampaignServerSide();
      return;
    }

    if (isPlatformBrowser(this.platformId)) {
      let redirectUrl = this.transferState.get(REDIRECTURL_KEY, '');
      if (redirectUrl !== '') {
        window.location.replace(redirectUrl);
        return;
      }

      const isError = this.transferState.get(ISERROR_KEY, false);
      if (isError) {
        const errorMessage = this.transferState.get(ERRORMESSAGE_KEY, 'An unknown error occurred.');
        this.redirectToErrorPage(errorMessage);
        return;
      }

      if (this.isBot) {
        return;
      }

      if (this.isShortLink) {
        this.redirectToErrorPage('This sharing link is unavailable. Please check the link or contact the event organiser.');
        return;
      }

      // Attribution can wait for a bounded fingerprint attempt; rendering cannot.
      this.fingerprintReady = this.ngZone.runOutsideAngular(() =>
        loadLandingPageFingerprint().then(fingerprint => { this.fingerprint = fingerprint; }),
      );

      this.campaignData = this.transferState.get(CAMPAIGNDATA_KEY, null);
      redirectUrl = this.transferState.get(REDIRECTURL_KEY, '');
      if (redirectUrl !== '') {
        window.location.href = redirectUrl;
        return;
      }

      if (!isCampaignData(this.campaignData)) {
        this.redirectToErrorPage();
        return;
      }

      this.initializeCampaignDisplayFromData();

      this.clientAccountId = this.campaignData.clientAccountId;
      this.campaignId = this.campaignData.campaignId;
      this.state.campaignId.set(this.campaignId);
      this.syncStatsContext();
      const influencerType =
        (this.campaignData.influencerType ?? '').length > 0
          ? this.campaignData.influencerType
          : (this.campaignData.influencerTypeEnum ?? '');

      if ((this.campaignData.otherInfluencerCount ?? 0) <= 2) {
        switch (influencerType) {
          case 'Influencer':
          case 'Influencers':
            this.influencerMessage.set(`Join the influencer community!`);
            break;
          default:
            this.influencerMessage.set(`Join the ${influencerType} influencer community!`);
            break;
        }
      } else {
        switch (influencerType) {
          case 'Influencer':
          case 'Influencers':
            this.influencerMessage.set(
              `Join ${this.campaignData.otherInfluencerCount} other influencers!`,
            );
            break;
          default:
            this.influencerMessage.set(
              `Join ${this.campaignData.otherInfluencerCount} other ${influencerType} influencers!`,
            );
            break;
        }
      }

      if (window.location.pathname.includes('/stats')) {
        this.toggleLeaderboard(true);
      }

      const responseDataJson = JSON.stringify(this.campaignData);
      sessionStorage.setItem('buzzwave-campaign-metadata', `[${responseDataJson}]`);

      // All good, set metadata
      const defaultNetwork = this.campaignData.shareDefaultChoice ?? 'LinkedIn';
      const defaultNetworkObj = this.shareNetworks().find(
        (network) => network.name.toLocaleLowerCase() === defaultNetwork.toLocaleLowerCase(),
      );
      this.selectedNetwork.set(defaultNetworkObj ?? this.shareNetworks()[0]);

      this.shareNetworks.update((networks) => {
        networks.forEach((network) => {
          if (network.name.toLocaleLowerCase() === defaultNetwork.toLocaleLowerCase()) {
            network.selected = true;
            const defaultIndex = networks.indexOf(network);
            if (defaultIndex > -1) {
              const [defaultNetwork] = networks.splice(defaultIndex, 1);
              networks.unshift(defaultNetwork);
            }
          }
          switch (network.name) {
            case ShareType.LinkedIn:
              network.display = this.campaignData.shareOnLinkedIn;
              break;
            case ShareType.Facebook:
              network.display = this.campaignData.shareOnFacebook;
              break;
            case ShareType.Instagram:
              network.display = this.campaignData.shareOnInstagram;
              break;
            case ShareType.Twitter:
              network.display = this.campaignData.shareOnTwitter;
              break;
            case ShareType.Slack:
              network.display = this.campaignData.shareOnSlack;
              break;
            case ShareType.Teams:
              network.display = this.campaignData.shareOnTeams;
              break;
            case ShareType.WhatsApp:
              network.display = this.campaignData.shareOnWhatsapp;
              break;
            case ShareType.Link:
              network.display = this.campaignData.shareOnLink;
              break;
            case ShareType.Email:
              network.display = this.campaignData.shareOnEmail;
              break;
          }
        });

        return networks;
      });

      const showCustomImage: boolean = this.campaignData.showCustomImage ?? false;

      this.campaignParticipantId = this.transferState.get(CAMPAIGNPARTICIPANTID_KEY, '');
      this.state.campaignParticipantId.set(this.campaignParticipantId);
      if (showCustomImage && isNullOrWhiteSpace(this.campaignParticipantId)) {
        console.warn(
          '[LandingPage][Browser] Missing campaignParticipantId for custom image flow, redirecting',
          {
            campaignId: this.campaignId,
          },
        );
        this.router.navigate(['/error'], {
          state: { errorMessage: 'Campaign participant ID is required for this campaign.' },
        });
        return;
      }
      this.campaignData.campaignParticipantId = this.campaignParticipantId;
      // this.postForwardUrl.set(this.campaignData.postForwardUrl);

      // Replace any body URLs with redirect URL
      const postBody = replaceMergeTags(this.campaignData.postBody, this.campaignData);
      const twitterBody = replaceMergeTags(this.campaignData.postBodyTwitter, this.campaignData);

      this.postBody.set(postBody);
      this.postBodyTwitter.set(twitterBody);

      // Initial set before replacement with short links
      this.postBodyWithShortLinks.set(postBody);
      this.postBodyTwitterWithShortLinks.set(twitterBody);

      const existingShortLinks = sessionStorage.getItem('buzzwave-short-links');
      if (existingShortLinks) {
        this.shortLinksAcquiredForNetworks = JSON.parse(existingShortLinks);

        // Find networks that are displayed but missing from existing short links
        const missingNetworks = this.shareNetworks().filter(
          (network) =>
            network.display &&
            !this.shortLinksAcquiredForNetworks.some(
              (existingNetwork) => existingNetwork.networkName === network.name,
            ),
        );

        if (missingNetworks.length > 0) {
          // Get short links for missing networks
          await this.getShortLinks(missingNetworks.map((n) => n.name));
        }

        const selectedNetworkShortLinks = this.shortLinksAcquiredForNetworks.find(
          (n) => n.networkName === this.selectedNetwork().name,
        );
        this.selectedNetworkCampaignShareId = selectedNetworkShortLinks!.campaignShareId;

        if (this.selectedNetwork().name === ShareType.Twitter) {
          this.postBodyTwitterWithShortLinks.set(selectedNetworkShortLinks!.postBody);
        } else {
          this.postBodyWithShortLinks.set(selectedNetworkShortLinks!.postBody);
        }
      } else {
        // Not awaited so it completes in the background
        void this.getShortLinks().catch(() => undefined);
      }

      if (showCustomImage) {
        if (isNullOrWhiteSpace(this.campaignData.participantPostImageUrl)) {
          this.postImageUrl.set(`${this.buzzwaveS3Url}/${this.campaignData.postImageUrl}`);
        } else {
          this.postImageUrl.set(
            `${this.buzzwaveS3Url}/${this.campaignData.participantPostImageUrl}`,
          );
        }
      } else if (this.campaignData.usePersonalisation) {
        if (!isNullOrWhiteSpace(this.campaignData.postPersonalisationImagePreviewUrl)) {
          this.postImageUrl.set(
            `${this.buzzwaveS3Url}/${this.campaignData.postPersonalisationImagePreviewUrl}`,
          );
        } else if (!isNullOrWhiteSpace(this.campaignData.postImageUrl)) {
          this.postImageUrl.set(`${this.buzzwaveS3Url}/${this.campaignData.postImageUrl}`);
        } else {
          this.postImageUrl.set('/assets/defaultImage6.png');
        }
      } else {
        if (isNullOrWhiteSpace(this.campaignData.postImageUrl)) {
          this.postImageUrl.set('/assets/defaultImage7.png');
        } else {
          this.postImageUrl.set(`${this.buzzwaveS3Url}/${this.campaignData.postImageUrl}`);
        }
      }

      if (isNullOrWhiteSpace(this.campaignData.logoUrl)) {
        this.eventImageUrl.set('/assets/defaultImage8.png');
      } else {
        this.eventImageUrl.set(`${this.buzzwaveS3Url}/${this.campaignData.logoUrl}`);
      }
      this.eventName.set(this.campaignData.eventName);
      this.sponsor.set(this.getSponsor(this.campaignData));
      const ogImageUrl =
        this.transferState.get(SHAREIMAGEURL_KEY, '') || this.getOgImageUrl(this.campaignData);
      const twitterImageUrl = this.getTwitterImageUrl(this.campaignData, ogImageUrl);
      this.setMetadata(
        this.campaignData,
        this.getPublicCampaignUrl(),
        ogImageUrl,
        twitterImageUrl,
      );
      this.campaignTitle.set(replaceMergeTags(this.campaignData.title, this.campaignData));
      this.campaignText.set(replaceMergeTags(this.campaignData.text, this.campaignData));
      this.incentiveTCs.set(replaceMergeTags(this.campaignData.incentiveTCs, this.campaignData));
      this.incentiveText.set(replaceMergeTags(this.campaignData.incentiveText, this.campaignData));
      this.buttonText.set(replaceMergeTags(this.campaignData.buttonText, this.campaignData));
      this.state.shareButtonText.set(this.campaignData.buttonText ?? 'Share Now');
      this.useIncentive.set(this.campaignData.useIncentive ?? false);
      this.usePersonalisation.set(this.campaignData.usePersonalisation ?? false);
      this.jobTitle = this.campaignData.jobTitle ?? '';
      this.company = this.campaignData.companyName ?? '';
      this.showBuzzwaveBranding.set(!(this.campaignData.hideBuzzwaveBranding ?? true));

      this.ngZone.runOutsideAngular(() => { void this.trackImpression(); });
    }
  }

  private async trackImpression(): Promise<void> {
    try {
      if (!sessionStorage.getItem('buzzwave-impression-saved')) {
        const response = await fetchWithBootstrapAuth(
          buildBuzzwavePublicUrl(BUZZWAVE_PUBLIC_PATHS.impressionTrack),
          {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({
              campaignId: this.campaignData.campaignId,
              clientAccountId: this.campaignData.clientAccountId,
              campaignParticipantId: this.campaignParticipantId,
              impressionType: 'landingpage',
            }),
          },
          this.campaignData.campaignId,
          BUZZWAVE_PUBLIC_PATHS.impressionTrack,
        );
        if (response.ok) {
          sessionStorage.setItem('buzzwave-impression-saved', 'true');
        }
      }
    } catch {
      // Tracking failures must not interrupt the sharing journey or mark it saved.
    }
  }

  private initializeCampaignDisplayFromData(isFacebookOrTwitterBot = false): void {
    if (this.campaignData === -1) {
      return;
    }

    this.clientAccountId = this.campaignData.clientAccountId;
    this.campaignId = this.campaignData.campaignId;
    this.state.campaignId.set(this.campaignId);
    this.syncStatsContext();

    const influencerType =
      (this.campaignData.influencerType ?? '').length > 0
        ? this.campaignData.influencerType
        : (this.campaignData.influencerTypeEnum ?? '');

    if ((this.campaignData.otherInfluencerCount ?? 0) <= 2) {
      switch (influencerType) {
        case 'Influencer':
        case 'Influencers':
          this.influencerMessage.set(`Join the influencer community!`);
          break;
        default:
          this.influencerMessage.set(`Join the ${influencerType} influencer community!`);
          break;
      }
    } else {
      switch (influencerType) {
        case 'Influencer':
        case 'Influencers':
          this.influencerMessage.set(
            `Join ${this.campaignData.otherInfluencerCount} other influencers!`,
          );
          break;
        default:
          this.influencerMessage.set(
            `Join ${this.campaignData.otherInfluencerCount} other ${influencerType} influencers!`,
          );
          break;
      }
    }

    const defaultNetwork = this.campaignData.shareDefaultChoice ?? 'LinkedIn';
    const defaultNetworkObj = this.shareNetworks().find(
      (network) => network.name.toLocaleLowerCase() === defaultNetwork.toLocaleLowerCase(),
    );
    this.selectedNetwork.set(defaultNetworkObj ?? this.shareNetworks()[0]);

    this.shareNetworks.update((networks) => {
      networks.forEach((network) => {
        if (network.name.toLocaleLowerCase() === defaultNetwork.toLocaleLowerCase()) {
          network.selected = true;
          const defaultIndex = networks.indexOf(network);
          if (defaultIndex > -1) {
            const [defaultShareNetwork] = networks.splice(defaultIndex, 1);
            networks.unshift(defaultShareNetwork);
          }
        }

        switch (network.name) {
          case ShareType.LinkedIn:
            network.display = this.campaignData.shareOnLinkedIn;
            break;
          case ShareType.Facebook:
            network.display = this.campaignData.shareOnFacebook;
            break;
          case ShareType.Instagram:
            network.display = this.campaignData.shareOnInstagram;
            break;
          case ShareType.Twitter:
            network.display = this.campaignData.shareOnTwitter;
            break;
          case ShareType.Slack:
            network.display = this.campaignData.shareOnSlack;
            break;
          case ShareType.Teams:
            network.display = this.campaignData.shareOnTeams;
            break;
          case ShareType.WhatsApp:
            network.display = this.campaignData.shareOnWhatsapp;
            break;
          case ShareType.Link:
            network.display = this.campaignData.shareOnLink;
            break;
          case ShareType.Email:
            network.display = this.campaignData.shareOnEmail;
            break;
        }
      });

      return networks;
    });

    this.campaignParticipantId = this.transferState.get(CAMPAIGNPARTICIPANTID_KEY, '');
    this.state.campaignParticipantId.set(this.campaignParticipantId);
    this.campaignData.campaignParticipantId = this.campaignParticipantId;
    // this.postForwardUrl.set(this.campaignData.postForwardUrl);

    const postBody = replaceMergeTags(this.campaignData.postBody, this.campaignData);
    const twitterBody = replaceMergeTags(this.campaignData.postBodyTwitter, this.campaignData);
    this.postBody.set(postBody);
    this.postBodyTwitter.set(twitterBody);
    this.postBodyWithShortLinks.set(postBody);
    this.postBodyTwitterWithShortLinks.set(twitterBody);

    const showCustomImage: boolean = this.campaignData.showCustomImage ?? false;
    if (showCustomImage) {
      this.postImageUrl.set(
        isNullOrWhiteSpace(this.campaignData.participantPostImageUrl)
          ? `${this.buzzwaveS3Url}/${this.campaignData.postImageUrl}`
          : `${this.buzzwaveS3Url}/${this.campaignData.participantPostImageUrl}`,
      );
    } else if (this.campaignData.usePersonalisation) {
      if (!isNullOrWhiteSpace(this.campaignData.postPersonalisationImagePreviewUrl)) {
        this.postImageUrl.set(
          `${this.buzzwaveS3Url}/${this.campaignData.postPersonalisationImagePreviewUrl}`,
        );
      } else if (!isNullOrWhiteSpace(this.campaignData.postImageUrl)) {
        this.postImageUrl.set(`${this.buzzwaveS3Url}/${this.campaignData.postImageUrl}`);
      } else {
        this.postImageUrl.set('/assets/defaultImage2.png');
      }
    } else {
      this.postImageUrl.set(
        isNullOrWhiteSpace(this.campaignData.postImageUrl)
          ? '/assets/defaultImage3.png'
          : `${this.buzzwaveS3Url}/${this.campaignData.postImageUrl}`,
      );
    }

    this.eventImageUrl.set(
      isNullOrWhiteSpace(this.campaignData.logoUrl)
        ? '/assets/defaultImage4.png'
        : `${this.buzzwaveS3Url}/${this.campaignData.logoUrl}`,
    );
    this.eventName.set(this.campaignData.eventName);
    this.sponsor.set(this.getSponsor(this.campaignData));
    const ogImageUrl =
      this.transferState.get(SHAREIMAGEURL_KEY, '') || this.getOgImageUrl(this.campaignData);
    const twitterImageUrl = this.getTwitterImageUrl(this.campaignData, ogImageUrl);
    this.setMetadata(
      this.campaignData,
      this.getPublicCampaignUrl(),
      ogImageUrl,
      twitterImageUrl,
      isFacebookOrTwitterBot
    );
    this.campaignTitle.set(replaceMergeTags(this.campaignData.title, this.campaignData));
    this.campaignText.set(replaceMergeTags(this.campaignData.text, this.campaignData));
    this.incentiveTCs.set(replaceMergeTags(this.campaignData.incentiveTCs, this.campaignData));
    this.incentiveText.set(replaceMergeTags(this.campaignData.incentiveText, this.campaignData));
    this.buttonText.set(replaceMergeTags(this.campaignData.buttonText, this.campaignData));
    this.state.shareButtonText.set(this.campaignData.buttonText ?? 'Share Now');
    this.useIncentive.set(this.campaignData.useIncentive ?? false);
    this.usePersonalisation.set(this.campaignData.usePersonalisation ?? false);
    this.jobTitle = this.campaignData.jobTitle ?? '';
    this.company = this.campaignData.companyName ?? '';
    this.showBuzzwaveBranding.set(!(this.campaignData.hideBuzzwaveBranding ?? true));
  }

  private async loadCampaignServerSide(): Promise<void> {
    if (this.skipLandingPageProcessing) {
      return;
    }

    const inactivePageState = this.transferState.get(INACTIVEPAGE_KEY, null);
    if (inactivePageState) {
      return;
    }

    this.currentRequestUrl = this.getPublicCampaignPath();
    const parsedRequestContext = this.parseRequestContext(this.currentRequestUrl);
    this.campaignId = parsedRequestContext.campaignId;
    this.campaignParticipantId =
      this.transferState.get(
        CAMPAIGNPARTICIPANTID_KEY,
        parsedRequestContext.campaignParticipantId ?? '',
      ) ?? '';
    this.currentRequestParse = {
      rawPath: this.currentRequestUrl,
      rawQueryString: '',
      campaignId: this.campaignId,
      campaignParticipantId: this.campaignParticipantId,
    };
    // const userAgent = (this.request?.headers?.get('user-agent') || '').toLowerCase();
    // this.isBot =
    //   userAgent.includes('facebookexternalhit') ||
    //   userAgent.includes('twitterbot') ||
    //   userAgent.includes('pinterest') ||
    //   userAgent.includes('slackbot') ||
    //   userAgent.includes('linkedinbot');
    // console.log('[LandingPage][SSR] User agent check', {
    //   userAgent,
    //   isBot: this.isBot,
    // });
    // const isFacebookOrTwitterBot =
    //   userAgent.includes('facebookexternalhit') || userAgent.includes('twitterbot');
    // this.transferState.set(ISBOT_KEY, this.isBot);
    this.isShortLink = this.transferState.get(ISSHORTLINK_KEY, false);
    const redirectUrl = this.transferState.get(REDIRECTURL_KEY, '');
    if (redirectUrl !== '') {
      if (this.isShortLink) {
        return;
      }
      return;
    }

    const serverCampaignData = isCampaignData(this.campaignData)
      ? this.campaignData
      : this.transferState.get(CAMPAIGNDATA_KEY, null);
    if (isCampaignData(serverCampaignData)) {
      this.campaignData = serverCampaignData;

      if (this.isBot) {
        return;
      }
    }

    if (isNullOrWhiteSpace(this.currentRequestParse.campaignId)) {
      this.redirectToErrorPage('Invalid campaign ID.');
      return;
    }

    const { campaignId, campaignParticipantId } = this.currentRequestParse;
    const data = this.transferState.get(CAMPAIGNDATA_KEY, null);
    const ogImageUrl = this.transferState.get(SHAREIMAGEURL_KEY, '') || this.getOgImageUrl(data);
    const twitterImageUrl = this.getTwitterImageUrl(data, ogImageUrl);

    if (!isCampaignData(data)) {
      this.redirectToErrorPage('Campaign data was not provided by the resolver.');
      return;
    }

    this.campaignData = data;
    this.campaignId = campaignId;
    this.campaignParticipantId = campaignParticipantId;
    this.currentCampaignLookupUrl = '';

    this.setMetadata(
      data,
      this.getPublicCampaignUrl(),
      ogImageUrl,
      twitterImageUrl
    );
    this.transferState.set(SHAREIMAGEURL_KEY, ogImageUrl);
  }

  private getPublicCampaignUrl(): string {
    return new URL(this.getPublicCampaignPath(), environment.buzzwaveGoUrl).toString();
  }

  private getPublicCampaignPath(): string {
    const url = new URL(this.router.url ?? '/', environment.buzzwaveGoUrl);
    url.searchParams.delete('previewToken');
    url.searchParams.delete('isTest');
    return `${url.pathname}${url.search}${url.hash}`;
  }

  private removePreviewTokenFromBrowserUrl(): void {
    const url = new URL(window.location.href);
    const previewToken = url.searchParams.get('previewToken')?.trim() ?? '';
    if (!previewToken) {
      return;
    }

    setBrowserPreviewToken(previewToken);
    url.searchParams.delete('previewToken');
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  }

  private isStaticAssetRequest(requestPath: string): boolean {
    return requestPath.startsWith('/assets/') || /\.[a-zA-Z0-9]{1,8}$/.test(requestPath);
  }

  private captureSourceSequenceContext(): void {
    const queryParams = new URL(this.router.url || '/', 'http://localhost').searchParams;
    this.sourceSequenceId = queryParams.get('buzzwaveSequenceId') || undefined;
    this.sourceSequenceStepId = queryParams.get('buzzwaveSequenceStepId') || undefined;
  }

  private parseRequestContext(requestPath: string): {
    campaignId: string;
    campaignParticipantId: string;
  } {
    const pathname = new URL(requestPath || '/', 'http://localhost').pathname;
    const segments = pathname.split('/').filter(Boolean);

    if (segments.length === 0) {
      return { campaignId: '', campaignParticipantId: '' };
    }

    const [campaignId, campaignParticipantId = ''] = segments;
    if (campaignParticipantId.toLowerCase() === 'stats') {
      return { campaignId, campaignParticipantId: '' };
    }

    return {
      campaignId,
      campaignParticipantId,
    };
  }

  ngOnDestroy(): void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }
  //#endregion

  private syncStatsContext(): void {
    const participantId = this.transferState.get(CAMPAIGNPARTICIPANTID_KEY, '') ?? '';
    this.state.setCampaignContext(this.campaignData, participantId);
    const path = this.route?.snapshot.url.length
      ? '/' + this.route.snapshot.url.map(segment => segment.path).join('/')
      : (this.router.url || this.transferState.get(REQUESTURL_KEY, ''));
    const statsRequested = /\/stats\/?$/.test(new URL(path || '/', 'http://localhost').pathname);
    this.showLeaderboard.set(statsRequested && this.canViewInfluencerStats());
    if (statsRequested && !this.canViewInfluencerStats() && isPlatformBrowser(this.platformId)) {
      window.history.replaceState({}, '', `${window.location.pathname.replace(/\/stats\/?$/, '')}${window.location.search}`);
    }
  }

  toggleLeaderboard(show = true): void {
    const available = show && this.canViewInfluencerStats();
    this.showLeaderboard.set(available);
    if (!isPlatformBrowser(this.platformId)) return;
    const base = window.location.pathname.replace(/\/stats\/?$/, '');
    const path = `${base}${available ? '/stats' : ''}${window.location.search}`;
    if (`${window.location.pathname}${window.location.search}` !== path) window.history.pushState({}, '', path);
  }

  //#region Share methods

  getWhatsAppFallbackUrl(postBody: string): string {
    const message = postBody.normalize();
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://api.whatsapp.com/send?text=${encodedMessage}`;
    return whatsappUrl;
  }

  public async share(selectedNetwork: string) {
    if (selectedNetwork === ShareType.LinkedIn) {
      this.usePersonalisation.set(this.campaignData.usePersonalisation ?? false);
      await this.shareToLinkedIn();
      return;
    }
    if (!this.shortLinksAcquiredForNetworks.some(network => network.networkName === selectedNetwork)) {
      this.spinner.showLoader();
      try {
        await this.getShortLinks([selectedNetwork]);
      } catch {
        return;
      } finally {
        this.spinner.hideLoader();
      }
    }
    if (selectedNetwork !== ShareType.LinkedIn) {
      this.usePersonalisation.set(false);
      if (selectedNetwork !== ShareType.Twitter) {
        this.postBodyWithShortLinks.set(
          this.shortLinksAcquiredForNetworks.find((n) => n.networkName === selectedNetwork)!
            .postBody,
        );
      } else {
        this.postBodyTwitterWithShortLinks.set(
          this.shortLinksAcquiredForNetworks.find((n) => n.networkName === selectedNetwork)!
            .postBody,
        );
      }
    } else {
      this.usePersonalisation.set(this.campaignData.usePersonalisation ?? false);
    }

    switch (selectedNetwork) {
      case ShareType.LinkedIn:
        this.shareToLinkedIn();
        break;
      case ShareType.Facebook:
        this.shareWithPopupPreview(ShareType.Facebook, 'on Facebook');
        break;
      case ShareType.Instagram:
        this.shareWithPopupPreview(ShareType.Instagram, 'on Instagram');
        break;
      case ShareType.Twitter:
        this.shareWithPopupPreview(ShareType.Twitter, 'on X (Twitter)');
        break;
      case ShareType.WhatsApp:
        this.shareWithPopupPreview(ShareType.WhatsApp, 'on WhatsApp');
        break;
      case ShareType.Slack:
        this.shareWithPopupPreview(ShareType.Slack, 'on Slack');
        break;
      case ShareType.Teams:
        this.shareWithPopupPreview(ShareType.Teams, 'on Teams');
        break;
      case ShareType.Link:
        this.shareWithPopupPreview(ShareType.Link, 'a link');
        break;
      case ShareType.Email:
        this.shareWithPopupPreview(ShareType.Email, 'via email');
    }
  }

  async shareWithPopupPreview(shareType: ShareType, targetName: string) {
    if (shareType === ShareType.Facebook) {
      // Prepare the SDK while the user reviews the Facebook sharing instructions.
      void this.fbService.loadFacebookSDK().catch(() => undefined);
    }
    const isMobile = /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);
    const isApple = /iPad|iPhone|iPod|Macintosh/.test(navigator.userAgent);
    const selectedNetworkMetadata = this.shareNetworks().find((n) => n.name === shareType);
    const shortLinksForNetwork = this.shortLinksAcquiredForNetworks.find(
      (n) => n.networkName === shareType,
    );
    if (shareType === ShareType.Twitter) {
      this.selectedNetworkCampaignShareId = shortLinksForNetwork?.campaignShareId ?? '';
      await navigator.clipboard.writeText(this.postBodyTwitterWithShortLinks());
    } else {
      this.selectedNetworkCampaignShareId = this.copyToClipboardNonTwitter(shareType, false);
    }

    const modal = document.createElement('div');
    modal.id = 'buzzwave-modal';
    const overlay = document.createElement('div');
    overlay.id = 'buzzwave-overlay';
    Object.assign(overlay.style, {
      position: 'fixed',
      top: '0',
      left: '0',
      width: '100%',
      height: '100%',
      backgroundColor: 'rgba(211, 211, 211, 0.50)', // light grey with some transparency
      zIndex: 999,
    });
    document.body.appendChild(overlay);

    Object.assign(modal.style, {
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      backgroundColor: 'white',
      color: 'black',
      padding: '20px',
      borderRadius: '8px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
      zIndex: 1000,
      'max-width': '500px',
      'min-width': '300px',
      width: '80%',
      'max-height': '90vh',
      overflowY: 'auto',
    });

    if (shareType !== ShareType.Instagram) {
      modal.innerHTML = `
      <span id="buzzwave-close-modal" class="buzzwave-close">×</span>
      <p style="font-size: large"><strong>Share ${targetName}</strong></p>
      <p style="display: flex; align-items: center;">
        The post text has been copied to your clipboard
      </p>`;
    }

    if (shareType === ShareType.Instagram) {
      let openingText =
        'The post text has been copied to your clipboard and the post image downloaded to your device';
      let instructionText = ``;

      if (isMobile && isApple) {
        openingText = 'The post text has been copied to your clipboard';
        instructionText = `
          <li style="margin-top: 0px">Press and hold the image, then save to photos</li>
          <li style="margin-top: 0px">Open ${shareType}</li>
          <li style="margin-top: 0px">Upload the image into ${shareType}</li>
          <li style="margin-top: 0px">Press and hold to paste the post text and share 🚀</li>
          <li style="margin-top: 0px">Click to confirm you posted ✅</li>
        `;
      } else if (isMobile) {
        instructionText = `
          <li style="margin-top: 0px">Open ${shareType}</li>
          <li style="margin-top: 0px">Upload the image into ${shareType}</li>
          <li style="margin-top: 0px">Press and hold to paste the post text and share 🚀</li>
          <li style="margin-top: 0px">Click to confirm you posted ✅</li>
        `;
      } else {
        instructionText = `
          <li style="margin-top: 0px">Open ${shareType}</li>
          <li style="margin-top: 0px">Upload the image into ${shareType}</li>
          <li style="margin-top: 0px">Paste the post text (Ctrl + V or ⌘ + V) and share 🚀</li>
          <li style="margin-top: 0px">Click to confirm you posted ✅</li>
        `;
      }

      modal.innerHTML += `
        <span id="buzzwave-close-modal" class="buzzwave-close">×</span>
        <p style="font-size: large"><strong>Share ${targetName}</strong></p>
        <p style="display: flex; align-items: center;">
         ${openingText}
        </p>
        <ol style="border: 1px solid #ccc; border-radius: 5px; padding-top: 12px; padding-bottom: 6px; margin-top: 12px; margin-bottom: 12px; padding-right: 6px;">
          ${instructionText}
        </ol>
        <div style="margin-top: 12px; display: flex; justify-content: space-around; flex-wrap: wrap;">
          <button id="modal-button-open-target" class="margin-top-0-5 centered-button primary-share-button ${shareType.toLocaleLowerCase()}">
            <img src="${
              selectedNetworkMetadata!.icon
            }" alt="Button icon" class="button-icon-large" />
            <span class="button-text">Open ${shareType}</span>
          </button>
          <button id="buzzwave-confirm-yes" class="margin-top-0-5 centered-button secondary-button">I've shared</button>
        </div>
      `;
    } else if (shareType !== ShareType.Link && shareType !== ShareType.Email) {
      const instructionText = isMobile
        ? isApple
          ? `<li style="margin-top: 0px">Tap below to open ${shareType}</li>
            <li style="margin-top: 0px">Create a new post</li>
            <li style="margin-top: 0px">Press and hold to paste the post text and share 🚀</li>
            <li style="margin-top: 0px">Tap to confirm you posted ✅</li>`
          : `<li style="margin-top: 0px">Tap below to open ${shareType}</li>
            <li style="margin-top: 0px">Press and hold to paste the post text and share 🚀</li>
            <li style="margin-top: 0px">Tap to confirm you posted ✅</li>`
        : `<li style="margin-top: 0px">Click below to open ${shareType}</li>
          <li style="margin-top: 0px">Paste your post (Ctrl + V or ⌘ + V) and share 🚀</li>
          <li style="margin-top: 0px">Click to confirm you posted ✅</li>`;

      modal.innerHTML += `
        <ol style="border: 1px solid #ccc; border-radius: 5px; padding-top: 12px; padding-bottom: 6px; margin-top: 12px; margin-bottom: 12px; padding-right: 6px;">
          ${instructionText}
        </ol>
        <div style="margin-top: 12px; display: flex; justify-content: space-around; flex-wrap: wrap;">
          <button id="modal-button-open-target" class="margin-top-0-5 centered-button primary-share-button ${shareType.toLocaleLowerCase()}">
            <img src="${
              selectedNetworkMetadata!.icon
            }" alt="Button icon" class="button-icon-large" />
            <span class="button-text">Open ${shareType}</span>
          </button>
          <button id="buzzwave-confirm-yes" class="margin-top-0-5 centered-button secondary-button">I've shared</button>
        </div>
      `;
    } else if (shareType === ShareType.Email) {
      if (isMobile) {
        modal.innerHTML += `
          <ul style="padding-top: 6px; margin-top: 12px; margin-bottom: 12px; padding-right: 6px;">
            <li style="margin-top: 0px">Open your email app ✉️</li>
            <li style="margin-top: 0px">Press and hold to paste the post text and share with your contacts 🚀</li>
            <li style="margin-top: 0px">Tap to confirm you posted ✅</li>
          </ul>
          <div style="margin-top: 12px; display: flex; justify-content: center;">
          <button id="buzzwave-confirm-yes" class="margin-top-0-5 centered-button secondary-button">I've shared</button>
          </div>
        `;
      } else {
        modal.innerHTML += `
          <ul style="padding-top: 6px; margin-top: 12px; margin-bottom: 12px; padding-right: 6px;">
            <li style="margin-top: 0px">Open your email application ✉️</li>
            <li style="margin-top: 0px">Paste your share text (Ctr-v or ⌘-v) and share with your contacts 🚀</li>
            <li style="margin-top: 0px">Click to confirm you posted ✅</li>
          </ul>
          <div style="margin-top: 12px; display: flex; justify-content: center;">
          <button id="buzzwave-confirm-yes" class="margin-top-0-5 centered-button secondary-button">I've shared</button>
          </div>
        `;
      }
    } else {
      const instructionText = isMobile
        ? 'Press and hold to paste the post text and share with your contacts 🚀'
        : 'Paste your share text (Ctr-v or ⌘-v) and share with your contacts 🚀';
      modal.innerHTML += `
        <ul style="padding-top: 6px; margin-top: 12px; margin-bottom: 12px; padding-right: 6px;">
          <li style="margin-top: 0px">${instructionText}</li>
          <li style="margin-top: 0px">${isMobile ? 'Tap' : 'Click'} to confirm you posted ✅</li>
        </ul>
        <div style="margin-top: 12px; display: flex; justify-content: center;">
          <button id="buzzwave-confirm-yes" class="margin-top-0-5 centered-button secondary-button">I've shared</button>
        </div>
      `;
    }

    const openTargetButton = modal.querySelector('#modal-button-open-target') as HTMLButtonElement | null;
    if (openTargetButton) {
      openTargetButton.addEventListener('click', async () => {
        if (shareType !== ShareType.Facebook) {
          await this.openTargetApp(shareType);
          return;
        }
        if (openTargetButton.disabled) return;
        const originalContent = openTargetButton.innerHTML;
        openTargetButton.disabled = true;
        openTargetButton.setAttribute('aria-busy', 'true');
        const label = openTargetButton.querySelector('.button-text');
        if (label) label.textContent = 'Opening Facebook…';
        try {
          await this.openTargetApp(shareType);
        } finally {
          openTargetButton.disabled = false;
          openTargetButton.removeAttribute('aria-busy');
          openTargetButton.innerHTML = originalContent;
        }
      });
    }

    const closeButton = modal.querySelector('#buzzwave-close-modal') as HTMLElement;
    const closeShareModal = () => {
      document.removeEventListener('keydown', handleShareModalEscape);
      modal.remove();
      overlay.remove();
    };
    const handleShareModalEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeShareModal();
      }
    };

    closeButton.addEventListener('click', closeShareModal);
    document.addEventListener('keydown', handleShareModalEscape);

    const yesButton = modal.querySelector('#buzzwave-confirm-yes') as HTMLElement;

    yesButton.addEventListener('click', async () => {
      const confirmShareDiv = document.getElementById('buzzwave-confirm-share');
      if (!confirmShareDiv) {
        let targetMessage = '';
        switch (targetName) {
          case ShareType.Link:
            targetMessage = 'a link';
            break;
          case ShareType.Twitter:
            targetMessage = 'on X (Twitter)';
            break;
          default:
            targetMessage = targetName;
        }

        let html = `
        <div id="buzzwave-confirm-share">
          <span id="buzzwave-close-modal1" class="buzzwave-close">×</span>
          <p style="font-size: large"><strong>Thank you for sharing ${targetMessage}</strong></p>`;

        if (
          this.campaignData.useIncentive &&
          !isNullOrWhiteSpace(this.campaignData.incentiveFulfillmentUrl)
        ) {
          const urlWithMergeTagsReplaced = replaceMergeTags(
            this.campaignData.incentiveFulfillmentUrl,
            this.campaignData,
          );
          html += `<p>Click <a href="${urlWithMergeTagsReplaced}" target="_blank">here</a> to claim your incentive</p>`;
        }

        html += `
          <p>We appreciate your support, please share again on other networks!</p>
          <button id="buzzwave-close-modal2" class="close-button">Close</button>
        </div>`;

        modal.innerHTML = html;

        const closeConfirmationModal = () => {
          document.removeEventListener('keydown', handleShareModalEscape);
          modal.remove();
          overlay.remove();
        };

        (modal.querySelector('#buzzwave-close-modal1') as HTMLElement | null)?.addEventListener(
          'click',
          closeConfirmationModal,
        );
        (modal.querySelector('#buzzwave-close-modal2') as HTMLElement | null)?.addEventListener(
          'click',
          closeConfirmationModal,
        );

        void (async () => {
          try {
            await fetchWithBootstrapAuth(
              buildBuzzwavePublicUrl(BUZZWAVE_PUBLIC_PATHS.shareIntentionUpgradedByUser),
              {
                method: 'POST',
                headers: { 'content-type': 'application/json' },
                body: JSON.stringify({
                  campaignId: this.campaignData.campaignId,
                  clientAccountId: this.campaignData.clientAccountId,
                  campaignShareId: this.selectedNetworkCampaignShareId,
                  newStatus: 'Share',
                }),
              },
              this.campaignData.campaignId,
              BUZZWAVE_PUBLIC_PATHS.shareIntentionUpgradedByUser,
            );
            this.shortLinksAcquiredForNetworks = this.shortLinksAcquiredForNetworks.filter(
              (n) => n.networkName !== shareType,
            );
            void this.getShortLinks([shareType]).catch(() => undefined);
          } catch {
            // Do nothing
          }
        })();
      }
    });

    document.body.appendChild(modal);

    if (shareType === ShareType.Instagram) {
      if (this.postImageUrl() && (isApple && isMobile) === false) {
        try {
          const response = await fetch(this.postImageUrl());
          if (response.ok) {
            const blob = await response.blob();
            const eventName = this.eventName() || 'Event';
            const fileName = `${eventName} post image${
              blob.type === 'image/png' ? '.png' : '.jpg'
            }`;
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
          }
        } catch (e) {
          console.error('Failed to fetch or rename post image:', e);
        }
      }
    }
  }

  public async openTargetApp(shareType: string) {
    const modal = document.getElementById('buzzwave-modal');

    let fallbackUrl = this.selectedNetwork().fallbackUrl ?? '';

    try {
      await fetchWithBootstrapAuth(
        buildBuzzwavePublicUrl(BUZZWAVE_PUBLIC_PATHS.shareIntentionUpgradedByUser),
        {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            campaignId: this.campaignData.campaignId,
            clientAccountId: this.campaignData.clientAccountId,
            campaignShareId: this.selectedNetworkCampaignShareId,
            newStatus: 'Intention',
          }),
        },
        this.campaignData.campaignId,
        BUZZWAVE_PUBLIC_PATHS.shareIntentionUpgradedByUser,
      );
    } catch {
      // Do nothing
    }

    // Let the user see the dialog before opening the app
    if (shareType !== ShareType.Link) {
      const popupWarningElement = document.getElementById('buzzwave-popup-warning');

      if (
        !popupWarningElement &&
        (shareType === ShareType.Twitter || shareType === ShareType.Facebook)
      ) {
        modal!.insertAdjacentHTML(
          'beforeend',
          `
          <p id="buzzwave-popup-warning" class="margin-top-1 margin-bottom-1">⚠️ Not seeing ${shareType}? Please ensure that popups are not blocked for this website.</p>`,
        );
      }

      if (shareType === ShareType.WhatsApp) {
        fallbackUrl = this.getWhatsAppFallbackUrl!(this.postBodyWithShortLinks()) ?? '';
      }

      const isMobile = /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      const isApple = /iPad|iPhone|iPod|Macintosh/.test(navigator.userAgent);
      let skipOpenTargetApp = false;

      if (shareType === ShareType.Instagram) {
        if (!isMobile) {
          if (/Mobi|Android|iPhone|iPad/i.test(navigator.userAgent)) {
            this.openInNewTab(fallbackUrl);
          } else {
            window.open(fallbackUrl, '_blank');
          }
          skipOpenTargetApp = true;
        }
        if (this.postImageUrl()) {
          if (isMobile && isApple) {
            fallbackUrl = `instagram://camera`;
          }
        }
      }

      const firstShortLink = this.shortLinksAcquiredForNetworks.find(
        (n) => n.networkName === shareType,
      )?.shortLinkIds[0];

      if (shareType === ShareType.Facebook) {
        const attemptFacebookWeb = true;
        if (isMobile) {
          window.location.href = `fb://facewebmodal/f?href=https://www.facebook.com/sharer/sharer.php?u=${environment.buzzwaveGoUrl}/${firstShortLink}`;
        }
        if (attemptFacebookWeb) {
          if (isSafari) {
            const text = this.EncodeQueryString({
              u: `${environment.buzzwaveGoUrl}/${firstShortLink}`,
              quote: this.postBodyWithShortLinks(),
            });
            this.openInNewTab(`https://www.facebook.com/sharer/sharer.php?${text}`);
          } else {
            await this.fbService.shareUrlOrImage(
              `${environment.buzzwaveGoUrl}/${firstShortLink}`,
              this.postBodyWithShortLinks(),
            );
          }
        }
      } else if (shareType === ShareType.Twitter) {
        const text = this.postBodyTwitterWithShortLinks();
        const textContainsUrl = text.match(/https?:\/\/[^\s]+/g);
        this.twitterService.share(
          isMobile,
          text,
          textContainsUrl ? '' : `${environment.buzzwaveGoUrl}/${firstShortLink}`,
          undefined,
          undefined,
        );
      } else if (shareType === ShareType.Teams) {
        if (isApple) {
          if (/Mobi|Android|iPhone|iPad/i.test(navigator.userAgent)) {
            this.openInNewTab(fallbackUrl);
          } else {
            window.open(fallbackUrl, '_blank');
          }
        } else {
          if (this.selectedNetwork().appUrl) {
            window.location.href = this.selectedNetwork().appUrl!;
          }

          setTimeout(() => {
            if (!document.hidden) {
              if (/Mobi|Android|iPhone|iPad/i.test(navigator.userAgent)) {
                this.openInNewTab(fallbackUrl);
              } else {
                window.open(fallbackUrl, '_blank');
              }
            }
          }, 2000); // wait ~2 seconds for app to open
        }
      } else if (!skipOpenTargetApp) {
        if (this.selectedNetwork().appUrl) {
          window.location.href = this.selectedNetwork().appUrl!;
        }

        setTimeout(() => {
          if (!document.hidden) {
            if (/Mobi|Android|iPhone|iPad/i.test(navigator.userAgent)) {
              this.openInNewTab(fallbackUrl);
            } else {
              window.open(fallbackUrl, '_blank');
            }
          }
        }, 2000); // wait ~2 seconds for app to open
      }
    }

  }
  //#endregion

  //#region Helper methods

  private async getShortLinks(specificNetworksToGet?: string[]) {
    const networks = specificNetworksToGet ?? this.shareNetworks().filter(network => network.display).map(network => network.name);
    const pending = networks.map(network => this.pendingShortLinks.get(network)).filter((request): request is Promise<void> => !!request);
    const missing = networks.filter(network => !this.pendingShortLinks.has(network));
    const request = missing.length ? this.createShortLinks(missing) : Promise.resolve();
    for (const network of missing) this.pendingShortLinks.set(network, request);
    try {
      await Promise.all([...pending, request]);
    } finally {
      for (const network of missing) {
        if (this.pendingShortLinks.get(network) === request) this.pendingShortLinks.delete(network);
      }
    }
  }

  private async createShortLinks(specificNetworksToGet: string[]) {
    await this.fingerprintReady;
    const shareIntentionUrl = buildBuzzwavePublicUrl(BUZZWAVE_PUBLIC_PATHS.shareIntentions);
    const originConversionId = resolveLandingPageConversionOrigin({
      clientAccountId: this.clientAccountId,
      campaignId: this.campaignId,
      campaignParticipantId: this.campaignParticipantId,
      url: this.router.url,
      storedOrigin: isPlatformBrowser(this.platformId) ? sessionStorage.getItem('buzzwave-conversionOrigin') : null,
    });

    const allTwitterUrls = Array.from(
      new Set(`${this.postBodyTwitter()}`.match(/https?:\/\/[^\s]+/g) || []),
    );
    const allUrls = Array.from(new Set(this.postBody().match(/https?:\/\/[^\s]+/g) || []));

    const response = await fetchWithBootstrapAuth(
      shareIntentionUrl,
      {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          campaignId: this.campaignId,
          clientAccountId: this.clientAccountId,
          campaignNetworks: specificNetworksToGet
            ? specificNetworksToGet
            : this.shareNetworks()
                .filter((n) => n.display)
                .map((n) => n.name),
          campaignParticipantId: this.campaignParticipantId,
          postForwardUrls: allUrls,
          ...(originConversionId ? { originConversionId } : {}),
          postForwardTwitterUrls: allTwitterUrls,
          fingerprint: this.fingerprint,
          isTest: false,
          shareSourceEnum: 'landingpage',
          buzzwaveGoUrl: environment.buzzwaveGoUrl,
          ...(this.sourceSequenceId && { sourceSequenceId: this.sourceSequenceId }),
          ...(this.sourceSequenceStepId && { sourceSequenceStepId: this.sourceSequenceStepId }),
        }),
      },
      this.campaignId,
      BUZZWAVE_PUBLIC_PATHS.shareIntentions,
    );

    if (response.status !== 201) {
      throw new Error('Failed to create share intentions');
    }

    const parsedResponse: {
      campaignShareId: string;
      networkName: string;
      shortLinks: { forwardUrl: string; shortLinkId: string }[];
    }[] = ((await response.json()) as any)['shortLinks'];

    if (this.shareNetworks().find((n) => n.name === ShareType.Twitter && n.display)) {
      const twitterMetadata = parsedResponse.find((n) => n.networkName === ShareType.Twitter);
      // Check that twitter is included in the response
      if (!isNullOrUndefined(twitterMetadata)) {
        let newBody = this.postBodyTwitter();
        twitterMetadata?.shortLinks.forEach((link) => {
          newBody = newBody.replaceAll(
            link.forwardUrl,
            `${environment.buzzwaveGoUrl}/${link.shortLinkId}`,
          );
        });
        this.postBodyTwitterWithShortLinks.set(newBody);
        if (this.selectedNetwork().name === ShareType.Twitter) {
          this.selectedNetworkCampaignShareId = twitterMetadata?.campaignShareId ?? '';
          this.postBodyWithShortLinks.set(newBody);
        }
        this.shortLinksAcquiredForNetworks.push({
          campaignShareId: twitterMetadata?.campaignShareId ?? '',
          networkName: ShareType.Twitter,
          postBody: this.postBodyTwitterWithShortLinks(),
          shortLinkIds: twitterMetadata?.shortLinks.map((link) => link.shortLinkId) ?? [],
        });
      }
    }

    for (const network of this.shareNetworks().filter(
      (n) => n.display && n.name !== ShareType.Twitter,
    )) {
      const networkMetadata = parsedResponse.find((n) => n.networkName === network.name);
      if (isNullOrUndefined(networkMetadata)) {
        // Not all networks may have been requested
        continue;
      }

      let newBody = this.postBody();

      networkMetadata?.shortLinks.forEach((link) => {
        newBody = newBody.replaceAll(
          link.forwardUrl,
          `${environment.buzzwaveGoUrl}/${link.shortLinkId}`,
        );
      });

      if (this.selectedNetwork().name === network.name) {
        this.selectedNetworkCampaignShareId = networkMetadata?.campaignShareId ?? '';
        this.postBodyWithShortLinks.set(newBody);
      }
      this.shortLinksAcquiredForNetworks.push({
        campaignShareId: networkMetadata?.campaignShareId ?? '',
        networkName: network.name,
        postBody: newBody,
        shortLinkIds: networkMetadata?.shortLinks.map((link) => link.shortLinkId) ?? [],
      });
    }
    sessionStorage.setItem(
      'buzzwave-short-links',
      JSON.stringify(this.shortLinksAcquiredForNetworks),
    );
  }

  copyToClipboardNonTwitter(shareType: string, showToast = true): string {
    const shortLinkDataForNetwork = this.shortLinksAcquiredForNetworks.find(
      (n) => n.networkName === shareType,
    );
    const shortLinkIds = shortLinkDataForNetwork?.shortLinkIds ?? [];

    if (shortLinkIds.length === 0) {
      return '';
    }
    try {
      navigator.clipboard.writeText(this.postBodyWithShortLinks());
      if (showToast) {
        this.showClipboardToast();
      }
      return shortLinkDataForNetwork?.campaignShareId ?? '';
    } catch (error: any) {
      console.error('Clipboard copy failed:', error);
      legacyCopy(this.postBodyWithShortLinks());
      return shortLinkDataForNetwork?.campaignShareId ?? '';
    }
  }

  showClipboardToast(message = 'Message copied to clipboard!') {
    // Create toast element
    const toast = document.createElement('div');
    toast.textContent = message;

    // Style the toast
    Object.assign(toast.style, {
      position: 'fixed',
      bottom: '-40px',
      left: '50%',
      transform: 'translateX(-50%)',
      backgroundColor: 'whitesmoke',
      color: 'black',
      padding: '12px 24px',
      borderRadius: '8px',
      fontSize: '16px',
      zIndex: 1000,
      boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
      opacity: '0',
      transition: 'bottom 0.4s ease, opacity 0.4s ease',
    });

    document.body.appendChild(toast);

    // Force layout re-flow so transition kicks in
    void toast.offsetWidth;

    // Animate in
    toast.style.bottom = '40px';
    toast.style.opacity = '1';

    // Animate out after 8s
    setTimeout(() => {
      toast.style.bottom = '-60px';
      toast.style.opacity = '0';
    }, 8000);

    // Remove from DOM after transition
    toast.addEventListener('transitionend', () => {
      if (toast.style.opacity === '0') {
        toast.remove();
      }
    });
  }

  // 1 Opens a modal that starts the LinkedIn auth to post workflow
  async shareToLinkedIn() {
    if (this.linkedInSharing()) return;
    this.linkedInSharing.set(true);
    this.linkedInShareError.set('');

    const errorMessage =
      'An error occurred while getting the LinkedIn authorization URL. Please try again later.';

    try {
      const shortLinksForNetwork = this.shortLinksAcquiredForNetworks.find(
        (n) => n.networkName === ShareType.LinkedIn,
      );
      let campaignShareId = shortLinksForNetwork?.campaignShareId;

      if (isNullOrUndefined(campaignShareId)) {
        await this.getShortLinks([ShareType.LinkedIn]);
        campaignShareId = this.shortLinksAcquiredForNetworks.find(
          (n) => n.networkName === ShareType.LinkedIn,
        )?.campaignShareId;
      }

      // Make a request to the CORS proxy to get the LinkedIn login HTML.
      const params = new URLSearchParams({
        campaignId: this.campaignId,
        clientAccountId: this.clientAccountId,
        jobTitle: this.jobTitle,
        company: this.company,
        participantId: this.campaignParticipantId,
        shareSourceEnum: 'landingpage',
        campaignShareId: campaignShareId ?? '',
        testMode: 'false',
      });
      const url = `${buildBuzzwavePublicUrl(BUZZWAVE_PUBLIC_PATHS.pixelGetLinkedInAuthUrl)}?${params}`;

      const result = await fetchWithBootstrapAuth(
        url,
        { method: 'GET' },
        this.campaignId,
        BUZZWAVE_PUBLIC_PATHS.pixelGetLinkedInAuthUrl,
      );

      if (!result.ok) throw new Error('Unable to start LinkedIn sharing');
      const response = await result.json();
      // Redirect the user to the LinkedIn authorization URL

      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      if (isSafari) {
        window.location.href = response.linkedInAuthUrl;
      } else if (/Mobi|Android|iPhone|iPad/i.test(navigator.userAgent)) {
        this.openInNewTab(response.linkedInAuthUrl);
      } else {
        const width = 600;
        const height = 700;
        const left = window.screenX + (window.innerWidth - width) / 2;
        const top = window.screenY + (window.innerHeight - height) / 2;
        window.open(
          response.linkedInAuthUrl,
          '_blank',
          `toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes,width=${width},height=${height},top=${top},left=${left}`,
        );
      }
    } catch (error) {
      console.error(error);
      this.linkedInShareError.set(errorMessage);
    } finally {
      this.linkedInSharing.set(false);
    }
  }

  public getFriendlyTitle(networkName: string): string {
    switch (networkName) {
      case ShareType.Link:
        return 'Copy a link';
      case ShareType.Twitter:
        return 'Share on X (Twitter)';
      case ShareType.Email:
        return 'Share via Email';
      default:
        return `Share on ${networkName}`;
    }
  }

  private openInNewTab(url: string) {
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';

    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  private setMetadata(
    data: any,
    url: string,
    ogImageUrl: string,
    twitterImageUrl: string,
    isFacebookOrTwitterBot = false,
    userAgent = '',
  ): void {
    const title = this.getMetaText(data.eventName, 'Buzzwave');
    const description = this.getMetaDescription(data.text);
    const imageAlt = `${title} image`;
    const canonicalUrl = (() => {
      try {
        const parsed = new URL(url, environment.buzzwaveGoUrl);
        parsed.searchParams.delete('fbdebug');
        return parsed.toString();
      } catch {
        return new URL('/', environment.buzzwaveGoUrl).toString();
      }
    })();
    
    twitterImageUrl = isNullOrWhiteSpace(twitterImageUrl) ? ogImageUrl : twitterImageUrl;

    // General
    this.titleService.setTitle(title);
    this.setNameMeta('description', description);
    this.setNameMeta('robots', 'max-image-preview:large');
    this.setPropertyMeta('fb:app_id', FB_APP_ID);
    this.setPropertyMeta('og:url', canonicalUrl);

    // Open graph
    this.metaService.removeTag("property='og:image'");
    this.metaService.removeTag("property='og:image:alt'");
    this.metaService.removeTag("property='og:image:width'");
    this.metaService.removeTag("property='og:image:height'");
    this.metaService.removeTag("property='og:image:type'");
    this.setPropertyMeta('og:description', description);
    this.setPropertyMeta('og:image', isFacebookOrTwitterBot ? twitterImageUrl : ogImageUrl);
    this.setPropertyMeta('og:image:alt', imageAlt);
    this.setPropertyMeta('og:image:width', '1200');
    this.setPropertyMeta('og:image:height', '628');
    this.setPropertyMeta('og:image:type', 'image/png');
    this.setPropertyMeta('og:site_name', 'Buzzwave');
    this.setPropertyMeta('og:title', title);
    this.setPropertyMeta('og:type', 'website');

    // Twitter
    this.metaService.removeTag("name='twitter:image'");
    this.metaService.removeTag("name='twitter:image:alt'");
    this.metaService.removeTag("name='twitter:card'");
    this.metaService.removeTag("name='twitter:title'");
    this.metaService.removeTag("name='twitter:description'");
    this.metaService.removeTag("name='twitter:url'");
    this.metaService.removeTag("property='twitter:title'");
    this.metaService.removeTag("property='twitter:image'");
    this.metaService.removeTag("property='twitter:image:alt'");
    this.metaService.removeTag("property='twitter:description'");
    this.metaService.removeTag("property='twitter:site'");
    this.metaService.removeTag("property='twitter:url'");
    this.metaService.removeTag("property='twitter:creator'");
    this.metaService.removeTag("property='twitter:card'");

    this.setNameMeta('twitter:title', title);
    this.setNameMeta('twitter:image', twitterImageUrl);
    this.setNameMeta('twitter:image:alt', imageAlt);
    this.setNameMeta('twitter:description', description);
    this.setNameMeta('twitter:url', canonicalUrl);
    this.setNameMeta('twitter:card', 'summary_large_image');

    this.setNameMeta('user-agent', userAgent);
  }

  private setNameMeta(name: string, content: string): void {
    this.metaService.updateTag({ name, content }, `name="${name}"`);
  }

  private setPropertyMeta(property: string, content: string): void {
    this.metaService.updateTag({ property, content }, `property="${property}"`);
  }

  private getOgImageUrl(data: any): string {
    if (!isCampaignData(data)) {
      return this.postImageUrl();
    }

    const base = this.buzzwaveS3Url;
    if (data.showCustomImage ?? false) {
      return isNullOrWhiteSpace(data.participantPostImageUrl)
        ? `${base}/${data.postImageUrl}`.replace(/ /g, '%20')
        : `${base}/${data.participantPostImageUrl}`.replace(/ /g, '%20');
    }

    if (data.usePersonalisation) {
      if (!isNullOrWhiteSpace(data.postPersonalisationImagePreviewUrl)) {
        return `${base}/${data.postPersonalisationImagePreviewUrl}`.replace(/ /g, '%20');
      }

      if (!isNullOrWhiteSpace(data.postImageUrl)) {
        return `${base}/${data.postImageUrl}`.replace(/ /g, '%20');
      }

      return '/assets/defaultImage6.png';
    }

    return isNullOrWhiteSpace(data.postImageUrl)
      ? '/assets/defaultImage7.png'
      : `${base}/${data.postImageUrl}`.replace(/ /g, '%20');
  }

  private getTwitterImageUrl(data: any, fallbackImageUrl: string): string {
    if (!isCampaignData(data)) {
      return fallbackImageUrl;
    }

    const base = this.buzzwaveS3Url;
    if (!isNullOrWhiteSpace(data.participantPostXImageUrl)) {
      return `${base}/${data.participantPostXImageUrl}`.replace(/ /g, '%20');
    }

    if (!isNullOrWhiteSpace(data.postImageXUrl)) {
      return `${base}/${data.postImageXUrl}`.replace(/ /g, '%20');
    }

    return fallbackImageUrl;
  }

  private getMetaDescription(value: unknown): string {
    return this.truncateMetaText(
      this.getMetaText(
        value,
        'Events are better with colleagues and friends. Share this invitation with your network.',
      ),
      160,
    );
  }

  private getMetaText(value: unknown, fallback: string): string {
    if (typeof value !== 'string') {
      return fallback;
    }

    const text = value.replace(/\s+/g, ' ').trim();
    return text.length > 0 ? text : fallback;
  }

  private truncateMetaText(value: string, maxLength: number): string {
    if (value.length <= maxLength) {
      return value;
    }

    return `${value.substring(0, maxLength - 3).trimEnd()}...`;
  }

  private getSponsor(data: any): { name: string; logo: string; message: string; url: string } | null {
    const sponsor = data?.sponsor;
    if (!sponsor || typeof sponsor !== 'object') return null;
    const name = typeof sponsor.name === 'string' ? sponsor.name.trim() : '';
    const logo = typeof sponsor.logo === 'string' ? sponsor.logo.trim() : '';
    const message = typeof sponsor.message === 'string' ? sponsor.message.trim() : '';
    const url = typeof sponsor.url === 'string' ? sponsor.url.trim() : '';
    if (!name || !logo || !message || !url) return null;
    return { name, logo: `${this.buzzwaveS3Url}/${logo}`, message, url };
  }

  private EncodeQueryString(inputParamsObj: any): string {
    const response = [];
    for (const paramObj in inputParamsObj) {
      if (Object.prototype.hasOwnProperty.call(inputParamsObj, paramObj)) {
        const param = inputParamsObj[paramObj];
        response.push(
          param !== null && typeof param === 'object'
            ? this.EncodeQueryString(param)
            : encodeURIComponent(paramObj) + '=' + encodeURIComponent(param),
        );
      }
    }
    return response.join('&');
  }

  // private getAllRouteParams(route: ActivatedRoute): Record<string, string> {
  //   const all: Record<string, string> = {};
  //   let r: ActivatedRoute | null = route;

  //   while (r) {
  //     Object.assign(all, r.snapshot.params);
  //     r = r.firstChild ?? null;
  //   }

  //   return all;
  // }

  //#endregion
}
